// components/layout/Header.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { User, Bell, Heart, ShoppingCart, Menu, Search, MessageCircle } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUserNotifications, getFavorites, getTotalUnreadCount, getCacheStats, forceRefresh } from '../../services/api';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';
import { getImageUrl } from '../../utils/imageUtils';
import MobileMenu from './MobileMenu';
import logo from '../../assets/logo.png';

// Cache keys
const CACHE_KEYS = {
  NOTIFICATIONS: 'header_notifications',
  FAVORITES: 'header_favorites',
  UNREAD_MESSAGES: 'header_unread_messages',
  UNREAD_NOTIFICATIONS: 'header_unread_notifications',
  LAST_FETCH: 'header_last_fetch'
};

// Cache duration in milliseconds (30 minutes)
const CACHE_DURATION = 30 * 60 * 1000;

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const lastFetchRef = useRef(null);
  
  // WebSocket connection for real-time updates
  const { isConnected, subscribeToNotifications } = useNotificationWebSocket();

  // Calculate unread count from notifications
  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Cache helper functions
  const getFromCache = (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      return null;
    }
  };

  const saveToCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // Ignore cache errors
    }
  };

  const shouldFetchData = () => {
    const lastFetch = localStorage.getItem(CACHE_KEYS.LAST_FETCH);
    if (!lastFetch) return true;
    
    const lastFetchTime = parseInt(lastFetch);
    const now = Date.now();
    return (now - lastFetchTime) > CACHE_DURATION;
  };

  const updateLastFetchTime = () => {
    localStorage.setItem(CACHE_KEYS.LAST_FETCH, Date.now().toString());
  };

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  }, [searchQuery, navigate]);

  const handleMobileSearch = useCallback((e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchQuery)}`);
      setMobileSearchQuery('');
      setShowMobileMenu(false);
    }
  }, [mobileSearchQuery, navigate]);

  // Real-time notification handlers
  const handleNewNotification = useCallback((notification, unreadCount) => {
    console.log('🔔 New notification received in header:', notification.title);
    
    // Add to notifications list
    setNotifications(prev => [notification, ...prev]);
    
    // Update unread count
    setUnreadNotifications(unreadCount);
    
    // Save to cache
    const updatedNotifications = [notification, ...notifications];
    saveToCache(CACHE_KEYS.NOTIFICATIONS, updatedNotifications);
    saveToCache(CACHE_KEYS.UNREAD_NOTIFICATIONS, unreadCount);
    
    // Show browser notification if not in notifications page
    if (Notification.permission === 'granted' && document.visibilityState !== 'visible') {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.id?.toString(),
        requireInteraction: ['order', 'dispute', 'payment'].includes(notification.type)
      });
      
      browserNotification.onclick = () => {
        window.focus();
        navigate('/notifications');
        browserNotification.close();
      };
    }
  }, [notifications]);

  const handleNotificationRead = useCallback((notificationId, unreadCount) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      )
    );
    setUnreadNotifications(unreadCount);
    saveToCache(CACHE_KEYS.UNREAD_NOTIFICATIONS, unreadCount);
  }, []);

  const handleUnreadCountUpdate = useCallback((count) => {
    setUnreadNotifications(count);
    saveToCache(CACHE_KEYS.UNREAD_NOTIFICATIONS, count);
  }, []);

  const handleAllNotificationsCleared = useCallback((unreadCount) => {
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, is_read: true }))
    );
    setUnreadNotifications(unreadCount);
    saveToCache(CACHE_KEYS.UNREAD_NOTIFICATIONS, unreadCount);
  }, []);

  const loadData = useCallback(async () => {
    if (!user?.user_id) {
      setNotifications([]);
      setFavorites([]);
      setUnreadMessages(0);
      setUnreadNotifications(0);
      return;
    }

    // Check if we should fetch fresh data
    const shouldFetch = shouldFetchData();
    
    if (!shouldFetch) {
      // Load from cache
      const cachedNotifications = getFromCache(CACHE_KEYS.NOTIFICATIONS) || [];
      const cachedFavorites = getFromCache(CACHE_KEYS.FAVORITES) || [];
      const cachedUnreadMessages = getFromCache(CACHE_KEYS.UNREAD_MESSAGES) || 0;
      const cachedUnreadNotifications = getFromCache(CACHE_KEYS.UNREAD_NOTIFICATIONS) || 0;
      
      setNotifications(cachedNotifications);
      setFavorites(cachedFavorites);
      setUnreadMessages(cachedUnreadMessages);
      setUnreadNotifications(cachedUnreadNotifications);
      return;
    }

    try {
      // Fetch fresh data
      const [notificationsData, favoritesData, messagesData] = await Promise.all([
        getUserNotifications().catch(() => ({ data: [] })),
        getFavorites(user.user_id).catch(() => ({ data: [] })),
        getTotalUnreadCount(user.user_id).catch(() => ({ combined_total: 0 }))
      ]);

      const newNotifications = notificationsData.data || [];
      const newFavorites = favoritesData.data || [];
      const newUnreadNotifications = notificationsData.unread_count || 0;
      
      // Extract unread messages count
      let newUnreadMessages = 0;
      if (messagesData?.combined_total !== undefined) {
        newUnreadMessages = messagesData.combined_total;
      } else if (messagesData?.total_unread !== undefined) {
        const buyerCount = messagesData.total_unread || 0;
        const sellerCount = messagesData.seller_unread || 0;
        newUnreadMessages = buyerCount + sellerCount;
      }

      // Update state
      setNotifications(newNotifications);
      setFavorites(newFavorites);
      setUnreadMessages(newUnreadMessages);
      setUnreadNotifications(newUnreadNotifications);

      // Save to cache
      saveToCache(CACHE_KEYS.NOTIFICATIONS, newNotifications);
      saveToCache(CACHE_KEYS.FAVORITES, newFavorites);
      saveToCache(CACHE_KEYS.UNREAD_MESSAGES, newUnreadMessages);
      saveToCache(CACHE_KEYS.UNREAD_NOTIFICATIONS, newUnreadNotifications);
      updateLastFetchTime();

    } catch (error) {
      console.error('Error loading header data:', error);
      
      // Fallback to cache if available
      const cachedNotifications = getFromCache(CACHE_KEYS.NOTIFICATIONS) || [];
      const cachedFavorites = getFromCache(CACHE_KEYS.FAVORITES) || [];
      const cachedUnreadMessages = getFromCache(CACHE_KEYS.UNREAD_MESSAGES) || 0;
      const cachedUnreadNotifications = getFromCache(CACHE_KEYS.UNREAD_NOTIFICATIONS) || 0;
      
      setNotifications(cachedNotifications);
      setFavorites(cachedFavorites);
      setUnreadMessages(cachedUnreadMessages);
      setUnreadNotifications(cachedUnreadNotifications);
    }
  }, [user]);

  // Effect to load data on mount and when user changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // WebSocket subscription for real-time updates
  useEffect(() => {
    if (!user?.user_id) return;
    
    console.log('📡 Setting up WebSocket subscription for notifications');
    
    const unsubscribe = subscribeToNotifications(
      handleNewNotification,
      handleNotificationRead,
      handleUnreadCountUpdate,
      handleAllNotificationsCleared
    );
    
    setWsConnected(isConnected);
    
    return () => {
      unsubscribe();
    };
  }, [user, subscribeToNotifications, handleNewNotification, handleNotificationRead, handleUnreadCountUpdate, handleAllNotificationsCleared, isConnected]);

  // Update WebSocket connection status
  useEffect(() => {
    setWsConnected(isConnected);
  }, [isConnected]);

  // Listen for cache refresh events from other components
  useEffect(() => {
    const handleRefreshData = () => {
      // Clear cache and force fresh fetch
      localStorage.removeItem(CACHE_KEYS.LAST_FETCH);
      loadData();
    };

    window.addEventListener('refreshHeaderData', handleRefreshData);
    
    return () => {
      window.removeEventListener('refreshHeaderData', handleRefreshData);
    };
  }, [loadData]);

  const handleSellClick = () => {
    if (user) {
      navigate('/store');
    } else {
      navigate('/store', { state: { from: 'store/listings/create-listing' } });
    }
  };

  // Refresh on tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && shouldFetchData()) {
        loadData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData]);

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Logo & Mobile Menu */}
            <div className="flex items-center gap-3">
              <button
                className="md:hidden p-2 hover:bg-[#f5f5f5] rounded-lg"
                onClick={() => setShowMobileMenu(true)}
              >
                <Menu className="w-6 h-6" />
              </button>

              <Link to="/" className="flex items-center gap-3">
                <div className="flex items-center justify-center">
                  <img 
                    src={logo} 
                    alt="EcoMart Logo" 
                    className="h-10 w-auto object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      const fallbackDiv = e.target.parentNode.querySelector('.logo-fallback');
                      if (fallbackDiv) fallbackDiv.style.display = 'flex';
                    }}
                  />
                  <div className="logo-fallback hidden bg-[#ff660e] p-2 rounded-lg">
                    <ShoppingCart className="w-7 h-7 text-white" />
                  </div>
                </div>
              </Link>
            </div>

            {/* Center: Search Bar (Desktop Only) */}
            <div className="hidden md:flex flex-1 max-w-2xl">
              <form onSubmit={handleSearch} className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products, stores, categories..."
                  className="w-full pl-5 pr-14 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#ff660e]"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#ff660e] text-white p-3 rounded-lg hover:opacity-90"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>
            </div>

            {/* Right: Icons & Actions */}
            <div className="flex items-center gap-3 sm:gap-4">              
              {/* Notifications Bell */}
              <Link to="/notifications" className="relative p-2 hover:bg-[#f5f5f5] rounded-lg group">
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center font-bold animate-pulse">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
                {/* WebSocket connection indicator */}
                {wsConnected && unreadNotifications === 0 && (
                  <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </Link>

              {/* Favorites */}
              <Link to="/favorites" className="relative p-2 hover:bg-[#f5f5f5] rounded-lg">
                <Heart className="w-5 h-5" />
                {favorites.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#ff660e] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {favorites.length > 99 ? '99+' : favorites.length}
                  </span>
                )}
              </Link>

              {/* Messages */}
              <Link to="/chats" className="relative p-2 hover:bg-[#f5f5f5] rounded-lg">
                <MessageCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#ff660e] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </Link>

              {user ? (
                <Link 
                  to="/profile" 
                  className="hidden md:flex items-center gap-2 p-2 hover:bg-[#f5f5f5] rounded-lg transition-colors"
                >
                  {user.profile_picture_url ? (
                    <img 
                      src={getImageUrl(user.profile_picture_url)} 
                      alt={`${user.first_name || user.username || 'User'}'s profile`}
                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        const fallbackDiv = document.createElement('div');
                        fallbackDiv.className = 'w-8 h-8 bg-[#ff660e] rounded-full flex items-center justify-center text-white text-sm font-bold';
                        fallbackDiv.textContent = user.first_name?.[0] || user.username?.[0] || 'U';
                        e.target.parentNode.appendChild(fallbackDiv);
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 bg-[#ff660e] rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {user.first_name?.[0] || user.username?.[0] || 'U'}
                    </div>
                  )}
                  <span className="hidden lg:inline text-sm font-medium">
                    Hi, {user.first_name || 'Account'}
                  </span>
                </Link>
              ) : (
                <div className="hidden md:flex items-center gap-3">
                  <Link to="/login" className="text-sm font-medium hover:text-[#ff660e]">
                    Sign In
                  </Link>
                  <Link to="/register" className="px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium hover:opacity-90">
                    Join Free
                  </Link>
                </div>
              )}

              <button
                onClick={handleSellClick}
                className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg font-bold hover:opacity-90 hidden sm:block"
              >
                SELL
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MobileMenu */}
      <MobileMenu
        showMobileMenu={showMobileMenu}
        setShowMobileMenu={setShowMobileMenu}
        user={user}
        favoritesCount={favorites.length}
        unreadNotifications={unreadNotifications}
        unreadMessages={unreadMessages}
        onSellClick={handleSellClick}
      />
    </>
  );
};

// Export helper function to force refresh from other components
export const refreshHeaderData = () => {
  window.dispatchEvent(new CustomEvent('refreshHeaderData'));
};

export default Header;